import { useState, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Send, Upload, X, FileText, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { AnimatedSection } from "@/components/AnimatedSection";

const comunas = [
  "Cerrillos", "Cerro Navia", "Conchalí", "El Bosque", "Estación Central",
  "Huechuraba", "Independencia", "La Cisterna", "La Florida", "La Granja",
  "La Pintana", "La Reina", "Las Condes", "Lo Barnechea", "Lo Espejo",
  "Lo Prado", "Macul", "Maipú", "Ñuñoa", "Pedro Aguirre Cerda",
  "Peñalolén", "Providencia", "Pudahuel", "Quilicura", "Quinta Normal",
  "Recoleta", "Renca", "San Bernardo", "San Joaquín", "San Miguel",
  "San Ramón", "Santiago", "Vitacura", "Puente Alto", "Otra"
];

const areasInteres = [
  "Electricista Industrial",
  "Electricista Comercial",
  "Técnico en Tableros Eléctricos",
  "Instalador de Iluminación",
  "Técnico en Redes y Comunicaciones",
  "Supervisor de Proyectos",
  "Ayudante Eléctrico",
  "Administrativo",
  "Otro"
];

const experienciaOptions = [
  "Sin experiencia",
  "1-2 años",
  "3-5 años",
  "Más de 5 años"
];

const disponibilidadOptions = [
  "Inmediata",
  "15 días",
  "1 mes",
  "Otro"
];

const formSchema = z.object({
  nombre: z.string().min(2, "El nombre debe tener al menos 2 caracteres").max(100, "Máximo 100 caracteres"),
  rut: z.string().min(8, "RUT inválido").max(12, "RUT inválido"),
  email: z.string().email("Email inválido").max(255, "Máximo 255 caracteres"),
  telefono: z.string().min(8, "Mínimo 8 dígitos").max(20, "Máximo 20 dígitos"),
  direccion: z.string().max(200, "Máximo 200 caracteres").optional(),
  comuna: z.string().min(1, "Selecciona una comuna"),
  areaInteres: z.string().min(1, "Selecciona un área"),
  experiencia: z.string().min(1, "Selecciona tu experiencia"),
  disponibilidad: z.string().min(1, "Selecciona disponibilidad"),
  licencia: z.enum(["si", "no"], { required_error: "Indica si tienes licencia" }),
  mensaje: z.string().max(500, "Máximo 500 caracteres").optional(),
  captcha: z.string().min(1, "Resuelve el captcha"),
});

type FormData = z.infer<typeof formSchema>;

// Helper function to convert file to Base64
const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result as string;
      // Remove the data URL prefix (e.g., "data:application/pdf;base64,")
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
  });
};

const CareersForm = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const captchaNumbers = useMemo(() => ({
    a: Math.floor(Math.random() * 10) + 1,
    b: Math.floor(Math.random() * 10) + 1,
  }), []);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      nombre: "",
      rut: "",
      email: "",
      telefono: "",
      direccion: "",
      comuna: "",
      areaInteres: "",
      experiencia: "",
      disponibilidad: "",
      mensaje: "",
      captcha: "",
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setFileError(null);

    if (!file) {
      setSelectedFile(null);
      return;
    }

    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ];

    if (!allowedTypes.includes(file.type)) {
      setFileError("Solo se permiten archivos PDF, DOC o DOCX");
      setSelectedFile(null);
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setFileError("El archivo no debe superar 5MB");
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
  };

  const removeFile = () => {
    setSelectedFile(null);
    setFileError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const onSubmit = async (data: FormData) => {
    // Validate captcha
    if (parseInt(data.captcha) !== captchaNumbers.a + captchaNumbers.b) {
      toast({
        title: "Error",
        description: "La respuesta del captcha es incorrecta",
        variant: "destructive",
      });
      return;
    }

    // Validate file
    if (!selectedFile) {
      setFileError("Debes adjuntar tu curriculum");
      return;
    }

    setIsSubmitting(true);

    try {
      // Convert CV to Base64
      const cvBase64 = await fileToBase64(selectedFile);

      // Prepare payload
      const payload = {
        formType: 'careers',
        nombre: data.nombre,
        rut: data.rut,
        email: data.email,
        telefono: data.telefono,
        direccion: data.direccion || '',
        comuna: data.comuna,
        areaInteres: data.areaInteres,
        experiencia: data.experiencia,
        disponibilidad: data.disponibilidad,
        licencia: data.licencia,
        mensaje: data.mensaje || '',
        cvFile: {
          name: selectedFile.name,
          type: selectedFile.type,
          data: cvBase64
        }
      };

      // Send to PHP endpoint
      const response = await fetch('/send-email.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (result.success) {
        toast({
          title: "¡Postulación enviada!",
          description: "Hemos recibido tu información. Te contactaremos pronto.",
        });
        form.reset();
        removeFile();
      } else {
        throw new Error(result.error || 'Error al enviar la postulación');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "No se pudo enviar la postulación. Intenta nuevamente.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="formulario" className="relative py-24 md:py-32 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-industrial-gray/95 via-dark-section to-dark-section" />
      
      {/* Animated blobs */}
      <div className="absolute top-1/4 left-1/4 w-80 h-80 bg-primary/20 rounded-full blur-3xl animate-pulse" />
      <div className="absolute bottom-1/3 right-1/4 w-64 h-64 bg-accent/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <AnimatedSection animation="fade-in" className="text-center mb-12">
          <span className="inline-block px-4 py-2 glass-liquid-medium rounded-full text-primary font-inter font-semibold text-sm mb-4">
            POSTULACIÓN
          </span>
          <h2 className="font-montserrat font-bold text-3xl md:text-4xl lg:text-5xl text-white mb-4">
            POSTULA A MYCING
          </h2>
          <p className="text-white/70 text-base md:text-lg max-w-2xl mx-auto">
            Completa el formulario con tus datos y adjunta tu curriculum
          </p>
        </AnimatedSection>

        {/* Form Container */}
        <AnimatedSection animation="slide-up" delay={200}>
          <div className="max-w-4xl mx-auto glass-liquid-dark-heavy rounded-3xl p-8 md:p-12 border border-white/10">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                {/* Personal Info */}
                <div className="grid md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="nombre"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-white/90">Nombre completo *</FormLabel>
                        <FormControl>
                          <Input placeholder="Juan Pérez González" {...field} className="bg-white/10 border-white/20 text-white" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="rut"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-white/90">RUT *</FormLabel>
                        <FormControl>
                          <Input placeholder="12.345.678-9" {...field} className="bg-white/10 border-white/20 text-white" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-white/90">Email *</FormLabel>
                        <FormControl>
                          <Input type="email" placeholder="correo@ejemplo.com" {...field} className="bg-white/10 border-white/20 text-white" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="telefono"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-white/90">Teléfono *</FormLabel>
                        <FormControl>
                          <Input type="tel" placeholder="+56 9 1234 5678" {...field} className="bg-white/10 border-white/20 text-white" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="direccion"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-white/90">Dirección</FormLabel>
                        <FormControl>
                          <Input placeholder="Av. Ejemplo 123" {...field} className="bg-white/10 border-white/20 text-white" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="comuna"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-white/90">Comuna *</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger className="bg-white/10 border-white/20 text-white">
                              <SelectValue placeholder="Selecciona comuna" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent className="max-h-60 z-50">
                            {comunas.map((comuna) => (
                              <SelectItem key={comuna} value={comuna}>{comuna}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Professional Info */}
                <div className="grid md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="areaInteres"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-white/90">Área de interés *</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger className="bg-white/10 border-white/20 text-white">
                              <SelectValue placeholder="Selecciona área" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent className="z-50">
                            {areasInteres.map((area) => (
                              <SelectItem key={area} value={area}>{area}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="experiencia"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-white/90">Años de experiencia *</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger className="bg-white/10 border-white/20 text-white">
                              <SelectValue placeholder="Selecciona experiencia" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent className="z-50">
                            {experienciaOptions.map((exp) => (
                              <SelectItem key={exp} value={exp}>{exp}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="disponibilidad"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-white/90">Disponibilidad *</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger className="bg-white/10 border-white/20 text-white">
                              <SelectValue placeholder="Selecciona disponibilidad" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent className="z-50">
                            {disponibilidadOptions.map((disp) => (
                              <SelectItem key={disp} value={disp}>{disp}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="licencia"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-white/90">¿Tienes licencia de conducir? *</FormLabel>
                        <FormControl>
                          <RadioGroup
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                            className="flex gap-6 pt-2"
                          >
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="si" id="licencia-si" className="border-white/40" />
                              <Label htmlFor="licencia-si" className="text-white/80">Sí</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="no" id="licencia-no" className="border-white/40" />
                              <Label htmlFor="licencia-no" className="text-white/80">No</Label>
                            </div>
                          </RadioGroup>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Message */}
                <FormField
                  control={form.control}
                  name="mensaje"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-white/90">Cuéntanos sobre ti (opcional)</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Describe tu experiencia, habilidades o motivación para unirte a MYCING..."
                          className="bg-white/10 border-white/20 text-white min-h-[120px]"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* File Upload */}
                <div className="space-y-4">
                  <Label className="text-white/90 text-base font-semibold flex items-center gap-2">
                    <FileText className="w-5 h-5 text-primary" />
                    Adjunta tu Curriculum *
                  </Label>
                  <div
                    className={`border-2 border-dashed rounded-2xl p-8 text-center transition-all duration-300 cursor-pointer ${
                      selectedFile
                        ? "border-green-500/50 bg-green-500/10"
                        : fileError
                        ? "border-red-500/50 bg-red-500/10"
                        : "border-white/20 hover:border-primary/50 hover:bg-white/5"
                    }`}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileChange}
                      accept=".pdf,.doc,.docx"
                      className="hidden"
                    />
                    
                    {selectedFile ? (
                      <div className="flex items-center justify-center gap-4">
                        <FileText className="w-10 h-10 text-green-500" />
                        <div className="text-left">
                          <p className="text-white font-medium">{selectedFile.name}</p>
                          <p className="text-white/60 text-sm">
                            {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeFile();
                          }}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                        >
                          <X className="w-5 h-5" />
                        </Button>
                      </div>
                    ) : (
                      <>
                        <Upload className="w-12 h-12 text-white/40 mx-auto mb-4" />
                        <p className="text-white/80 font-medium">
                          Haz clic para subir tu CV
                        </p>
                        <p className="text-white/50 text-sm mt-2">
                          PDF, DOC o DOCX (máx. 5MB)
                        </p>
                      </>
                    )}
                  </div>
                  {fileError && (
                    <p className="text-red-400 text-sm">{fileError}</p>
                  )}
                </div>

                {/* Captcha */}
                <FormField
                  control={form.control}
                  name="captcha"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-white/90">
                        Verificación: ¿Cuánto es {captchaNumbers.a} + {captchaNumbers.b}? *
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          placeholder="Tu respuesta"
                          {...field}
                          className="bg-white/10 border-white/20 text-white max-w-[200px]"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Submit Button */}
                <div className="pt-4">
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full md:w-auto px-12 py-6 text-lg font-bold bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90 text-white rounded-2xl shadow-liquid-lg hover:shadow-liquid-xl transition-all duration-300"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Enviar Postulación
                      </>
                    )}
                  </Button>
                </div>

                {/* Privacy Note */}
                <p className="text-white/50 text-sm text-center">
                  Al enviar este formulario, aceptas que tus datos sean procesados para fines de selección de personal.
                  Tu información será tratada de forma confidencial.
                </p>
              </form>
            </Form>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default CareersForm;
